import { useLayoutEffect, useRef } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMap } from "@fortawesome/pro-regular-svg-icons/faMap";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons/faMagnifyingGlass";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons/faCalendar";
import { faGrid2 } from "@fortawesome/pro-regular-svg-icons/faGrid2";
import { faMap as faMapSolid } from "@fortawesome/pro-solid-svg-icons/faMap";
import { faMagnifyingGlass as faMagnifyingGlassSolid } from "@fortawesome/pro-solid-svg-icons/faMagnifyingGlass";
import { faCalendar as faCalendarSolid } from "@fortawesome/pro-solid-svg-icons/faCalendar";
import { faGrid2 as faGrid2Solid } from "@fortawesome/pro-solid-svg-icons/faGrid2";
import { cn } from "@wondermaps/ui";
import type { VisitorSearchParams, VisitorTab } from "../../lib/visitor-search";

interface TabDef {
  id: VisitorTab;
  labelKey: string;
  icon: typeof faMap;
  activeIcon: typeof faMap;
}

const TAB_DEFS: TabDef[] = [
  { id: "map", labelKey: "nav.map", icon: faMap, activeIcon: faMapSolid },
  { id: "search", labelKey: "nav.search", icon: faMagnifyingGlass, activeIcon: faMagnifyingGlassSolid },
  { id: "events", labelKey: "nav.events", icon: faCalendar, activeIcon: faCalendarSolid },
  { id: "more", labelKey: "nav.more", icon: faGrid2, activeIcon: faGrid2Solid },
];

interface BottomNavBarProps {
  activeTab: VisitorTab;
  brandAccent?: string | null;
}

export function BottomNavBar({ activeTab, brandAccent }: BottomNavBarProps) {
  // Route-agnostic: component is used from /$mapSlug and /$orgSlug/$mapSlug.
  // Read current search via strict:false and merge; no functional updater (can't
  // be typed across two routes).
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as VisitorSearchParams;
  const { t } = useTranslation("map");
  const ref = useRef<HTMLDivElement>(null);

  // Set --bottom-nav-height on :root; auto-includes safe-area-inset-bottom via pb-safe
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      document.documentElement.style.setProperty(
        "--bottom-nav-height",
        `${el.offsetHeight}px`,
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--bottom-nav-height");
    };
  }, []);

  const handleTabClick = (tab: VisitorTab) => {
    // Category param pairs with map+search (POI browsing). Drop it when entering
    // events/more so a stale filter (e.g. ?category=saved) doesn't carry across
    // unrelated contexts and hide POIs after a "View on Map" hop.
    const stripCategory = tab === "events" || tab === "more";
    void navigate({
      to: ".",
      search: {
        ...search,
        tab,
        poi: undefined,
        view: undefined,
        ...(stripCategory ? { category: undefined } : {}),
      },
    });
  };

  return (
    <div
      ref={ref}
      className="absolute bottom-0 inset-x-0 z-30 bg-background/95 backdrop-blur-sm border-t pb-safe"
    >
      <div className="flex">
        {TAB_DEFS.map(({ id, labelKey, icon, activeIcon }) => {
          const isActive = activeTab === id;
          const label = t(labelKey);
          return (
            <button
              key={id}
              type="button"
              onClick={() => handleTabClick(id)}
              className="relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[52px]"
              aria-current={isActive ? "page" : undefined}
              aria-label={label}
            >
              {isActive && (
                <span
                  aria-hidden
                  className={cn(
                    "absolute inset-x-3 rounded-full pointer-events-none",
                    !brandAccent && "bg-foreground/10",
                  )}
                  style={
                    brandAccent
                      ? { backgroundColor: brandAccent, opacity: 0.16, top: 4, bottom: 4 }
                      : { top: 4, bottom: 4 }
                  }
                />
              )}
              <FontAwesomeIcon
                icon={isActive ? activeIcon : icon}
                className={cn(
                  "h-5 w-5",
                  isActive ? "text-foreground" : "text-muted-foreground",
                )}
                style={isActive && brandAccent ? { color: brandAccent } : undefined}
                aria-hidden
              />
              <span
                className={cn(
                  "text-[10px] leading-none",
                  isActive
                    ? "font-semibold text-foreground"
                    : "font-medium text-muted-foreground",
                )}
                style={isActive && brandAccent ? { color: brandAccent } : undefined}
                aria-hidden
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
